import { useCallback, useEffect, useMemo, useRef } from 'react';

import { bindAll, type UnbindFn } from 'bind-event-listener';

import type { DocNode } from '@atlaskit/adf-schema';
import type { MediaProvider } from '@atlaskit/editor-common/provider-factory';
import type { PublicPluginAPI } from '@atlaskit/editor-common/types';
import type { AnalyticsPlugin } from '@atlaskit/editor-plugin-analytics';
import type { LocalIdPlugin } from '@atlaskit/editor-plugin-local-id';
import type { Schema } from '@atlaskit/editor-prosemirror/model';
import { fg } from '@atlaskit/platform-feature-flags';
import { ExperienceName as RovoExperienceName } from '@atlassian/conversation-assistant-instrumentation/constants/experience-tracker';
import type { AIPlugin } from '@atlassian/editor-plugin-ai';
import type { AiStreamingOrchestratorPlugin } from '@atlassian/editor-plugin-ai-streaming-orchestrator';
import {
	type ClientId,
	getBrowserBridgeAPI,
	type RovoContentBridgeAPI,
} from '@atlassian/rovo-content-bridge-api';
import {
	ContentDestroyCommand,
	ContentReadyCommand,
	getConfluenceEditorClientId,
	ROVO_CHAT_CLIENT_ID,
} from '@atlassian/rovo-content-bridge-api-commands';
import { sendAIFCExperienceTrackingToParent } from '@atlassian/rovo-content-bridge-api-experience-tracking-sender';

import type {
	BuildContentContextFn,
	PassThroughCommandProps,
	StreamAbortCallback,
	StreamCallback,
	StreamEndCallback,
	StreamErrorCallback,
	StreamRepairCallback,
	StreamStartCallback,
	StreamTimeoutCallback,
} from './command-handlers/command-handler';
import { CommandManager } from './command-manager';
import type { AddCommandHandlersFn } from './types/command-manager';
import type { AdditionalContext, ContentLocation } from './types/content';
import type { CreateMediaProviderFn, UploadImageErrorCallback } from './types/media';
import { useMediaImageProcessor } from './useMediaImageProcessor';
import type { MediaNode } from './useMediaImageProcessor';
import { log } from './utils/aifc-logging';

const bindRovoBridgeTeardown = (teardown: () => void): UnbindFn => {
	const run = () => {
		/**
		 * @description We do NOT want to yield a return value here. It can cause certain unload
		 * handlers to display a window alert with "Are you sure you want to leave this page?".
		 * Specifically, see @see https://developer.mozilla.org/en-US/docs/Web/API/Window/beforeunload_event,
		 * which is remarked to be a cancellable event by browsers.
		 */
		teardown();
		// Although unnecessary (the JS context is being destroyed), we remove the event listeners
		// to avoid any potential memory leaks due to hanging references - side effects can cause this.
		unbind();
	};

	// Below, we attempt to teardown on different event handlers which we know will trigger
	// on complete unmount of the document in this JS context. Note: Same-origin iframes are
	// indeed considered their own JS context here; just that they have access to other contexts.
	// Browsers report `unload` and `beforeunload` to be unreliable. Hence, we leverage a chain
	// of these events. See official MDN documentation for more (linked below).

	/**
	 * @see https://developer.mozilla.org/en-US/docs/Web/API/Window/beforeunload_event
	 * TODO - bring beforeunload back by consolidating handlers per discussion at -
	 * https://bitbucket.org/atlassian/atlassian-frontend-monorepo/pull-requests/207747/overview#comment-674441389
	 */
	// window.addEventListener('beforeunload', run, { once: true });

	/**
	 * @see https://developer.mozilla.org/en-US/docs/Web/API/Window/unload_event
	 * @see https://developer.mozilla.org/en-US/docs/Web/API/Window/pagehide_event
	 */
	const unbind = bindAll(window, [
		{
			type: 'unload',
			listener: run,
			options: { once: true },
		},
		{
			type: 'pagehide',
			listener: run,
			options: { once: true },
		},
	]);

	return unbind;
};

function useRovoBridgeLifecycle(
	props: UseEditorRovoBridgeProps & {
		clientId: ClientId;
		rovoBridgeAPI: RovoContentBridgeAPI | null;
	},
) {
	const contentType = 'page';
	const { clientId, contentLocation, contentId, rovoBridgeAPI } = props;

	useEffect(() => {
		if (!rovoBridgeAPI) {
			return;
		}

		rovoBridgeAPI.ready(clientId);
		rovoBridgeAPI.send(ContentReadyCommand, {
			senderId: clientId,
			receiverId: ROVO_CHAT_CLIENT_ID,
			contentId,
			contentType,
			canProvideContext: true,
			contentContext: contentLocation,
		});

		function rovoBridgeTeardown() {
			if (!rovoBridgeAPI) {
				return;
			}

			rovoBridgeAPI.send(ContentDestroyCommand, {
				senderId: clientId,
				receiverId: ROVO_CHAT_CLIENT_ID,
				contentId,
				contentType,
				contentContext: contentLocation,
			});
			rovoBridgeAPI.teardown(clientId);
		}

		const unbind = bindRovoBridgeTeardown(() => rovoBridgeTeardown());

		return () => {
			try {
				unbind();
				rovoBridgeTeardown();
				// TODO: EDITOR-123 - clear inactivity timeouts?
			} catch {
				// TODO: EDITOR-123 - handle errors; should go to Sentry
				// could be sent in future by navigation lockout
			}
		};
	}, [clientId, contentLocation, contentId, contentType, rovoBridgeAPI]);
}

export type UseEditorRovoBridgeProps = {
	addCommandHandlers?: AddCommandHandlersFn;
	additionalContext?: AdditionalContext;
	buildContentContext?: BuildContentContextFn;
	contentId: string;
	contentLocation: ContentLocation;
	createMediaProvider?: CreateMediaProviderFn;
	editorAPI:
		| PublicPluginAPI<[AnalyticsPlugin, AiStreamingOrchestratorPlugin, AIPlugin, LocalIdPlugin]>
		| undefined
		| null;
	mediaProvider?: Promise<MediaProvider>;
	onStream?: StreamCallback;
	onStreamAbort?: StreamAbortCallback;
	onStreamEnd?: StreamEndCallback;
	onStreamError?: StreamErrorCallback;
	onStreamRepair?: StreamRepairCallback;
	onStreamStart?: StreamStartCallback;
	onStreamTimeout?: StreamTimeoutCallback;
	onUploadImageError?: UploadImageErrorCallback;
	useGenericEditorSkill?: boolean;
};

/**
 * React hook that manages the Rovo bridge connection for AI streaming functionality in the Atlassian Editor.
 *
 * This hook establishes a bidirectional communication bridge between the editor and Rovo chat,
 * enabling AI-powered content generation, editing, and streaming operations. It handles the complete
 * lifecycle of the bridge connection including setup, command registration, and cleanup.
 *
 * Typically this is required for use with the `aiStreamingOrchestratorPlugin` and the free gen prompt field
 *
 * @param props - Configuration properties for the Rovo bridge
 * @param props.contentLocation - The location context of the content ('global' | 'staging-area')
 * @param props.contentId - Unique identifier for the content being edited
 * @param props.editorAPI - The editor's plugin API, must include AnalyticsPlugin and AiStreamingOrchestratorPlugin
 * @param props.useGenericEditorSkill - [Recommended] Optional to enable the generic editor skill for products that don't have a specific minion
 * @param props.additionalContext - Optional additional context to pass to ReadContentCommand
 * @param props.buildContentContext - Optional function to build content context for AI operations
 * @param props.mediaProvider - Optional media provider
 * @param props.createMediaProvider - Optional function to create a media provider
 * @param props.onStreamStart - Optional callback when AI streaming starts
 * @param props.onStream - Optional callback for each streaming chunk received
 * @param props.onStreamEnd - Optional callback when AI streaming completes successfully
 * @param props.onStreamAbort - Optional callback when AI streaming is aborted
 * @param props.onStreamTimeout - Optional callback when AI streaming times out
 * @param props.onStreamError - Optional callback when AI streaming encounters an error
 * @param props.onStreamRepair - Optional callback for AI stream repair operations
 * @param props.onUploadImageError - Optional callback to handle upload image errors
 * @param props.addCommandHandlers - Optional function to add custom command handlers
 *
 * @example
 * ```tsx
 * import { useEditorRovoBridge } from '@atlassian/editor-rovo-bridge';
 *
 * function MyEditor() {
 *   const { preset, editorApi: editorAPI } = usePreset(...);
 *
 *   useEditorRovoBridge({
 *     contentLocation: 'global',
 *     contentId: 'page-123',
 *     editorAPI,
 *     useGenericEditorSkill: true,
 *     additionalContext: {
 *       isViewMode: true,
 *     },
 *     onStreamStart: ({ streamType, payload }) => {
 *       console.log('AI streaming started:', streamType);
 *     },
 *     onStream: ({ streamType, payload }) => {
 *       console.log('AI streaming data:', payload);
 *     },
 *     onStreamEnd: ({ streamType, adfDoc }) => {
 *       console.log('AI streaming completed with ADF:', adfDoc);
 *     },
 *     onStreamError: ({ error, streamType }) => {
 *       console.error('AI streaming error:', error);
 *     },
 *   });
 *
 *   return <ComposableEditor preset={preset} />;
 * }
 * ```
 */
export const useEditorRovoBridge = ({
	contentLocation,
	contentId,
	editorAPI,
	useGenericEditorSkill,
	additionalContext,
	buildContentContext,
	mediaProvider,
	createMediaProvider,
	onUploadImageError,
	onStreamStart,
	onStream,
	onStreamEnd,
	onStreamAbort,
	onStreamTimeout,
	onStreamError,
	onStreamRepair,
	addCommandHandlers,
}: UseEditorRovoBridgeProps) => {
	const contentType = 'page';
	const clientId = getConfluenceEditorClientId(contentId);

	useEffect(() => {
		if (!editorAPI) {
			return;
		}
		editorAPI.aiExperience?.actions.updateTargetId(contentId);
	}, [editorAPI, contentId]);

	const rovoBridgeAPI = useMemo(() => {
		if (!editorAPI) {
			return null;
		}

		return getBrowserBridgeAPI();
	}, [editorAPI]);

	const imageProcessor = useMediaImageProcessor({
		mediaProvider,
		createMediaProvider,
		onUploadImageError,
	});

	const reuploadImages: PassThroughCommandProps['reuploadImages'] = useCallback(
		async (adfDoc?: DocNode) => {
			const processImagesAndUpdateDocument = async () => {
				if (!imageProcessor?.processImagesInAdf) {
					throw new Error(
						'Image processor not available for image re-uploading in editor streaming',
					);
				}
				if (!editorAPI) {
					throw new Error('Editor API not available for image re-uploading in editor streaming');
				}

				if (fg('confluence_frontend_aifc_reupload_image')) {
					const editorDoc = await editorAPI.core.actions.requestDocumentAsync();
					if (!editorDoc) {
						throw new Error('Could not get document from editor for image re-uploading');
					}

					const { error, hasChanges, changedNodesByLocalId } =
						await imageProcessor.processImagesInAdf(editorDoc as DocNode);

					log('reuploadImages', { error, hasChanges, adfDoc, changedNodesByLocalId });

					if (error) {
						throw error;
					}

					if (hasChanges && changedNodesByLocalId && changedNodesByLocalId.size > 0) {
						// Get the editor's schema to convert ADF to ProseMirror nodes
						const schema: Schema | undefined = editorAPI.core.sharedState.currentState()?.schema;
						if (!schema) {
							throw new Error('Could not get editor schema for image re-uploading');
						}
						// Replace individual nodes
						changedNodesByLocalId.forEach((adfNode: MediaNode, localId: string) => {
							// Convert ADF to ProseMirror node
							const pmNode = schema.nodeFromJSON(adfNode);
							editorAPI?.localId?.actions.replaceNode({
								localId,
								value: pmNode,
							});
						});
					}
				} else {
					if (!adfDoc) {
						throw new Error('adfDoc is required for image re-uploading when feature flag is off');
					}
					const { error, hasChanges, processedAdf } =
						await imageProcessor.processImagesInAdf(adfDoc);

					log('reuploadImages', { error, hasChanges, processedAdf, adfDoc });

					if (error) {
						throw error;
					}

					if (hasChanges) {
						editorAPI?.core.actions.replaceDocument(processedAdf, {
							scrollIntoView: false,
						});
					}
				}
			};

			try {
				await processImagesAndUpdateDocument();
			} catch (error) {
				void sendAIFCExperienceTrackingToParent({
					status: 'fail',
					actionType: 'page',
					actionId: contentId,
					contentContext: contentLocation,
					experienceName: RovoExperienceName.AIFC_ALL_DATA_HANDLED,
					error: (error as Error).message,
				});

				onUploadImageError?.({ error: error as Error });
			}
		},
		[imageProcessor, editorAPI, contentId, contentLocation, onUploadImageError],
	);

	const commandProps = useMemo<PassThroughCommandProps | null>(() => {
		if (!editorAPI) {
			return null;
		}
		return {
			clientId,
			contentLocation,
			contentId,
			contentType,
			editorAPI,
			useGenericEditorSkill,
			additionalContext,
			buildContentContext,
			onStreamStart,
			onStream,
			onStreamEnd,
			onStreamAbort,
			onStreamTimeout,
			onStreamError,
			onStreamRepair,
			reuploadImages,
		};
	}, [
		clientId,
		contentLocation,
		contentId,
		contentType,
		editorAPI,
		useGenericEditorSkill,
		additionalContext,
		buildContentContext,
		onStreamStart,
		onStream,
		onStreamEnd,
		onStreamAbort,
		onStreamTimeout,
		onStreamError,
		onStreamRepair,
		reuploadImages,
	]);

	useEffect(() => {
		if (fg('platform_editor_aifc_rovo_bridge_deps_update')) {
			return;
		}
		if (!editorAPI || !commandProps || !rovoBridgeAPI) {
			return;
		}
		const commandManager = new CommandManager(clientId, rovoBridgeAPI, commandProps);

		if (addCommandHandlers) {
			addCommandHandlers({ commandManager });
		} else {
			commandManager.addDefaultCommandHandlers();
		}

		commandManager.registerCommands();
		return () => {
			commandManager.deregisterCommands();
		};
	}, [
		clientId,
		contentLocation,
		rovoBridgeAPI,
		commandProps,
		editorAPI,
		buildContentContext,
		addCommandHandlers,
	]);

	const commandManagerRef = useRef<CommandManager | null>(null);

	useEffect(() => {
		if (!editorAPI || !rovoBridgeAPI || !commandProps) {
			return;
		}

		// only initialize command manager once because
		// createCommandProcessor is stateful and doesn't support re-creation for now
		if (!commandManagerRef.current && fg('platform_editor_aifc_rovo_bridge_deps_update')) {
			commandManagerRef.current = new CommandManager(
				clientId,
				rovoBridgeAPI,
				commandProps,
				addCommandHandlers,
			);
			commandManagerRef.current.addAndRegisterCommands();
		}
	}, [editorAPI, clientId, rovoBridgeAPI, commandProps, addCommandHandlers]);

	// clientId shouldn't change, but just in case
	// when it changes, we re-register the commands to the updated clientId
	useEffect(() => {
		if (!commandManagerRef.current || !rovoBridgeAPI) {
			return;
		}
		commandManagerRef.current.updateClientId(clientId, rovoBridgeAPI);
	}, [clientId, rovoBridgeAPI]);

	// when commandProps changes, we update the command props
	useEffect(() => {
		if (!commandManagerRef.current || !commandProps) {
			return;
		}
		commandManagerRef.current.updateCommandProps(commandProps);
	}, [commandProps]);

	// when addCommandHandlers changes, we deregister and register the commands
	useEffect(() => {
		if (!commandManagerRef.current) {
			return;
		}

		commandManagerRef.current.updateAddCommandHandlers(addCommandHandlers);
	}, [addCommandHandlers]);

	// when the component unmounts, we deregister the commands
	useEffect(() => {
		return () => {
			commandManagerRef.current?.deregisterCommands();
		};
	}, []);

	useRovoBridgeLifecycle({
		contentLocation,
		contentId,
		editorAPI,
		buildContentContext,
		onStreamStart,
		onStream,
		onStreamEnd,
		onStreamAbort,
		onStreamTimeout,
		onStreamError,
		onStreamRepair,
		addCommandHandlers,
		clientId,
		rovoBridgeAPI,
	});
};
